/**
 * The product's name, written the way the marketing site writes it.
 *
 * TWO WORDS, TWO COLOURS. "Standing" in whatever ink surrounds it, "Docs" in
 * seal red — the same red as the stamp on the app icon, and the same value
 * standingdocs.com uses (site/src/components/Wordmark.tsx). The application
 * used to set the whole name in one weight of ink, so the product looked like
 * two different products either side of the sign-in page.
 *
 * ONE DRAWING FOR THE ICON. `favicon.svg` is already shipped and is rendered
 * from the master in `branding/icon/`, so this points at that file rather than
 * inlining the glyph a second time. Change the master, re-render, and the
 * sidebar follows; there is no copy here to fall behind.
 *
 * The icon is decorative: the name is right beside it in text, so repeating it
 * to a screen reader would only be noise.
 */

import styles from './Wordmark.module.css';

interface Props {
  /**
   * For the console's chrome bar, which is dark in BOTH themes and so cannot
   * use the theme-following seal. Everywhere else takes the default.
   */
  onChrome?: boolean;
  /** The icon beside the name. Off where the name is a line of prose. */
  withIcon?: boolean;
  className?: string;
}

export function Wordmark({ onChrome = false, withIcon = false, className }: Props) {
  const name = (
    <>Standing<span className={onChrome ? styles.onChrome : styles.docs}>Docs</span></>
  );

  if (!withIcon) return <span className={className}>{name}</span>;

  /*
   * WITH THE ICON, THE CALLER'S CLASS DRESSES THE WHOLE LOCKUP, not just the
   * name. Two things follow, and both are wanted:
   *
   * A font-size set there is the em the ICON is measured in, so the mark grows
   * with the name by default and a placement does not have to size both.
   *
   * And a placement can set `--wordmark-icon` to break that link where the two
   * should not scale together — a sign-in card wants a large mark above a
   * heading-sized name, and four times the icon at 1.45em would put the name
   * at 72px and push it off the card.
   */
  return (
    <span className={[styles.row, className].filter(Boolean).join(' ')}>
      <img src="/favicon.svg" alt="" aria-hidden="true" className={styles.icon} />
      <span>{name}</span>
    </span>
  );
}
